/**
 * 12-Month Performance Dashboard
 * Main dashboard showing macro trends, verified profit, and efficiency metrics
 */

import React, { useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, Clock, Package,
  Users, AlertTriangle, CheckCircle, RefreshCw
} from 'lucide-react';
import { useAnalytics } from './AnalyticsContext';

const Dashboard12Month = () => {
  const {
    dashboardData,
    dashboardLoading,
    dashboardError,
    fetchDashboard,
    formatCurrency,
    formatPercentage
  } = useAnalytics();

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (dashboardLoading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (dashboardError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700">{dashboardError}</p>
        <button
          onClick={fetchDashboard}
          className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!dashboardData) return null;

  // Prepare chart data
  const chartData = dashboardData.months.map(month => ({
    month: new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    revenue: Math.round(month.revenue.netService),
    labor: Math.round(month.labor.totalCost),
    profit: Math.round(month.profit.trueMonthly),
    confidence: month.dataConfidenceScore
  }));

  const efficiencyData = dashboardData.trends.hoursPer1000Units.map(item => ({
    month: new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'short' }),
    value: item.value
  }));

  // Calculate trends
  const currentMonth = dashboardData.months[dashboardData.months.length - 1];
  const previousMonth = dashboardData.months[dashboardData.months.length - 2];
  const profitTrend = previousMonth ?
    ((currentMonth.profit.trueMonthly - previousMonth.profit.trueMonthly) / Math.abs(previousMonth.profit.trueMonthly)) * 100 : 0;
  const revenueTrend = previousMonth ?
    ((currentMonth.revenue.netService - previousMonth.revenue.netService) / Math.abs(previousMonth.revenue.netService)) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Profit Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            {profitTrend >= 0 ? (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>+{profitTrend.toFixed(1)}%</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <TrendingDown className="w-4 h-4" />
                <span>{profitTrend.toFixed(1)}%</span>
              </div>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-4">
            {formatCurrency(dashboardData.totals.totalProfit)}
          </p>
          <p className="text-sm text-gray-500 mt-1">12-Month Total Profit</p>
        </div>

        {/* Revenue Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            {revenueTrend >= 0 ? (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>+{revenueTrend.toFixed(1)}%</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-red-600 text-sm">
                <TrendingDown className="w-4 h-4" />
                <span>{revenueTrend.toFixed(1)}%</span>
              </div>
            )}
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-4">
            {formatCurrency(dashboardData.totals.totalServiceRevenue)}
          </p>
          <p className="text-sm text-gray-500 mt-1">12-Month Service Revenue</p>
        </div>

        {/* Labor Cost Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-4">
            {formatCurrency(dashboardData.totals.totalLabor)}
          </p>
          <p className="text-sm text-gray-500 mt-1">12-Month Labor Cost</p>
        </div>

        {/* Data Confidence Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              {dashboardData.totals.avgConfidenceScore >= 80 ? (
                <CheckCircle className="w-6 h-6 text-purple-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              )}
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-4">
            {formatPercentage(dashboardData.totals.avgConfidenceScore)}
          </p>
          <p className="text-sm text-gray-500 mt-1">Avg Data Confidence</p>
        </div>
      </div>

      {/* Verified vs Pending Profit */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Verified vs Pending Profit (Current Month)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* True Profit */}
          <div className="text-center">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(currentMonth.profit.trueMonthly)}
            </p>
            <p className="text-sm text-gray-500 mt-1">True Monthly Profit</p>
          </div>

          {/* Verified Profit */}
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              {formatCurrency(currentMonth.profit.verified)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Verified Profit</p>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${currentMonth.dataConfidenceScore}%` }}
              />
            </div>
          </div>

          {/* Pending Profit */}
          <div className="text-center">
            <p className="text-3xl font-bold text-yellow-600">
              {formatCurrency(currentMonth.profit.pending)}
            </p>
            <p className="text-sm text-gray-500 mt-1">Pending Verification</p>
            <p className="text-xs text-gray-400 mt-2">
              Items in Audit Queue
            </p>
          </div>
        </div>
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue vs Labor Stacked Area Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Revenue vs Labor (12 Months)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <Tooltip
                formatter={(value, name) => [
                  `$${value.toLocaleString()}`,
                  name === 'revenue' ? 'Service Revenue' : 'Labor Cost'
                ]}
                contentStyle={{
                  backgroundColor: '#FFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stackId="1"
                stroke="#10B981"
                fill="#10B981"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="labor"
                name="Labor"
                stackId="2"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Profit Trend Line Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Monthly Profit Trend
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                tickFormatter={(value) => `$${(value/1000).toFixed(0)}k`}
                tick={{ fontSize: 12, fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <Tooltip
                formatter={(value) => [`$${value.toLocaleString()}`, 'Profit']}
                contentStyle={{
                  backgroundColor: '#FFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px'
                }}
              />
              <Line
                type="monotone"
                dataKey="profit"
                name="Profit"
                stroke="#8B5CF6"
                strokeWidth={3}
                dot={{ fill: '#8B5CF6', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Efficiency Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Efficiency: Hours per 1,000 Units
            </h2>
            <p className="text-sm text-gray-500">Lower is better</p>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-5 h-5" />
            <span className="text-2xl font-bold">
              {currentMonth.efficiency.hoursPer1000Units.toFixed(1)}
            </span>
            <span className="text-sm">hrs/1k units</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={efficiencyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#6B7280' }}
              axisLine={{ stroke: '#E5E7EB' }}
              domain={['auto', 'auto']}
            />
            <Tooltip
              formatter={(value) => [`${value.toFixed(2)} hours`, 'Per 1,000 Units']}
              contentStyle={{
                backgroundColor: '#FFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px'
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#F59E0B"
              strokeWidth={2}
              dot={{ fill: '#F59E0B', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
          <Package className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-gray-900">
            {dashboardData.totals.totalUnits.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500">Total Units Processed</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
          <Clock className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-gray-900">
            {dashboardData.totals.totalHours.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500">Total Labor Hours</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
          <DollarSign className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-gray-900">
            {formatCurrency(dashboardData.totals.totalRevenue)}
          </p>
          <p className="text-xs text-gray-500">Gross Revenue</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
          <TrendingUp className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-xl font-bold text-gray-900">
            {((dashboardData.totals.totalProfit / dashboardData.totals.totalRevenue) * 100).toFixed(1)}%
          </p>
          <p className="text-xs text-gray-500">Profit Margin</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard12Month;
